import type {
  ExtensionKey,
  FilterContext,
  NativeIdPath,
  TargetDefinition,
  Trigger,
} from '@packages/systems/ix3/schema';
import {targetTransforms} from '@packages/systems/ix3/schema';
import {
  IX3_WF_EXTENSION_KEYS,
  isInstPathTargetKey,
} from '@packages/systems/ix3-wf-plugins/shared';
import type {
  SelectorScope,
  DirectScope,
} from '@packages/systems/ix3-wf-plugins/shared';
import {repairMissingFilterBy} from './migrations';

export type IdMap = Record<string, string>;

export type RemapOptions = {
  /** If provided, wf:inst componentId uses this instead of idMap lookup */
  destinationComponentId?: string;
  /** If true, unmapped wf:class IDs are removed instead of preserved (XSCP behavior) */
  removeUnmappedClasses?: boolean;
};

const buildTargetDefinition = (
  extensionKey: ExtensionKey,
  value: unknown,
  filterContext?: FilterContext
): TargetDefinition => {
  return filterContext
    ? [extensionKey, value, filterContext]
    : [extensionKey, value];
};

const remapByMap = (value: string, map: IdMap): string => map[value] || value;

function isNativeIdPath(value: unknown): value is NativeIdPath {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'string' &&
    typeof value[1] === 'string'
  );
}

function isScopedNativeIdPath(
  value: unknown
): value is [NativeIdPath, SelectorScope | DirectScope] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    Array.isArray(value[0]) &&
    isNativeIdPath(value[0])
  );
}

function isScopedSelectorValue(
  value: unknown
): value is [string[], SelectorScope | DirectScope] {
  return Array.isArray(value) && value.length === 2 && Array.isArray(value[0]);
}

const remapClassValue = (
  id: string,
  styleBlocksIdMap: IdMap,
  removeUnmapped: boolean
): string | undefined => {
  const mapped = styleBlocksIdMap[id];
  if (mapped) return mapped;
  return removeUnmapped ? undefined : id;
};

const remapTargetValue = (
  extensionKey: ExtensionKey,
  value: unknown,
  idMap: IdMap,
  styleBlocksIdMap: IdMap,
  options?: RemapOptions
): unknown => {
  if (extensionKey === IX3_WF_EXTENSION_KEYS.ID) {
    if (typeof value === 'string') {
      return remapByMap(value, idMap);
    }
    if (Array.isArray(value) && value.length > 0) {
      const [id, ...rest] = value;
      return typeof id === 'string' ? [remapByMap(id, idMap), ...rest] : value;
    }
    return value;
  }

  if (extensionKey === IX3_WF_EXTENSION_KEYS.CLASS) {
    if (typeof value === 'string') {
      return remapByMap(value, styleBlocksIdMap);
    }
    if (Array.isArray(value)) {
      const removeUnmapped = options?.removeUnmappedClasses === true;

      if (isScopedSelectorValue(value)) {
        const [styleBlockIds, scope] = value;
        const remapped = removeUnmapped
          ? styleBlockIds.flatMap((id: string) => {
              const mapped = remapClassValue(id, styleBlocksIdMap, true);
              return mapped != null ? [mapped] : [];
            })
          : styleBlockIds.map((id: string) => remapByMap(id, styleBlocksIdMap));
        return [remapped, scope];
      }

      if (removeUnmapped) {
        return (value as string[]).flatMap((id) => {
          const mapped = remapClassValue(id, styleBlocksIdMap, true);
          return mapped != null ? [mapped] : [];
        });
      }
      return (value as string[]).map((id) => remapByMap(id, styleBlocksIdMap));
    }
    return value;
  }

  // The popover targets address a popover with the same `[componentId, elementId]`
  // shape `wf:inst` uses, so they remap identically. Without this they fell
  // through untouched, and duplicating or detaching a component containing an
  // animated popover left the copy's Popover/Backdrop action targets naming the
  // ORIGINAL popover while its trigger had been remapped to the new one.
  //
  // Backdrop no longer stores a path — it is resolved from the interaction at
  // runtime — so this only reaches it for data written before that change. An
  // empty value is not an array and falls through untouched.
  if (isInstPathTargetKey(extensionKey) && Array.isArray(value)) {
    const destCompId = options?.destinationComponentId;

    if (isScopedNativeIdPath(value)) {
      const [nativeIdPath, scope] = value;
      const [componentId, elementId] = nativeIdPath;
      const newComponentId = destCompId ?? remapByMap(componentId, idMap);
      return [[newComponentId, remapByMap(elementId, idMap)], scope];
    }

    if (isNativeIdPath(value)) {
      const [componentId, elementId] = value;
      const newComponentId = destCompId ?? remapByMap(componentId, idMap);
      return [newComponentId, remapByMap(elementId, idMap)];
    }

    if (value.length >= 2) {
      const [componentId, elementId, ...rest] = value as [
        string,
        string,
        ...unknown[],
      ];
      const newComponentId = destCompId ?? remapByMap(componentId, idMap);
      return [newComponentId, remapByMap(elementId, idMap), ...rest];
    }
    return value;
  }

  return value;
};

const remapFilterContext = (
  filterContext: FilterContext | undefined,
  idMap: IdMap,
  styleBlocksIdMap: IdMap,
  options?: RemapOptions
): FilterContext | undefined => {
  if (!filterContext?.filterBy || !Array.isArray(filterContext.filterBy)) {
    return filterContext;
  }

  const [filterByKey, filterByValue] = filterContext.filterBy;
  if (typeof filterByKey !== 'string') {
    return filterContext;
  }

  let remappedValue = remapTargetValue(
    filterByKey as ExtensionKey,
    filterByValue,
    idMap,
    styleBlocksIdMap,
    options
  );

  if (
    filterByKey === IX3_WF_EXTENSION_KEYS.CLASS &&
    Array.isArray(remappedValue)
  ) {
    remappedValue = (remappedValue as string[]).map((id) => idMap[id] || id);
  }

  return {
    ...filterContext,
    filterBy: [filterByKey, remappedValue],
  };
};

export const remapActionTarget = (
  target: TargetDefinition,
  idMap: IdMap,
  styleBlocksIdMap: IdMap,
  options?: RemapOptions
): TargetDefinition => {
  const repaired = repairMissingFilterBy(target);
  const extensionKey = repaired[0];
  const value = repaired[1];
  const filterContext = targetTransforms.getFilter(repaired);
  const remappedFilterContext = remapFilterContext(
    filterContext,
    idMap,
    styleBlocksIdMap,
    options
  );

  const buildTarget = (key: ExtensionKey, val: unknown) =>
    buildTargetDefinition(key, val, remappedFilterContext);

  if (
    extensionKey === IX3_WF_EXTENSION_KEYS.ID ||
    extensionKey === IX3_WF_EXTENSION_KEYS.CLASS ||
    // `wf:inst` plus the targets built on the same addressing — see
    // `remapTargetValue` and `INST_PATH_TARGET_KEYS`.
    isInstPathTargetKey(extensionKey)
  ) {
    return buildTarget(
      extensionKey,
      remapTargetValue(extensionKey, value, idMap, styleBlocksIdMap, options)
    );
  }

  return buildTarget(extensionKey, value);
};

export const remapTrigger = (
  trigger: Trigger,
  idMap: IdMap,
  styleBlocksIdMap: IdMap,
  options?: RemapOptions
): Trigger => {
  // Two items is a valid trigger: a targetless one — Page load, which has no
  // `defaultTarget` — is `[key, config]`. Bailing at `< 3` meant the
  // `assignedInstances` remap below never ran for the very shape it was added
  // for, so a copied targetless trigger kept the SOURCE ids and the
  // interaction was rejected by `findAssignedInstancesError`.
  if (!Array.isArray(trigger) || trigger.length < 2) return trigger;

  const triggerKey = trigger[0];
  const triggerConfig = trigger[1];
  const targetDef = trigger[2];

  // `assignedInstances` names popovers by the same `[componentId, elementId]`
  // path the targets use, so it has to travel through the same maps. Computed
  // BEFORE the target guard below: Page load is authored without a
  // `defaultTarget`, so a targetless trigger carrying a narrowing returned here
  // with the SOURCE ids while its sibling popover triggers were remapped — and
  // the copied interaction was then rejected by `findAssignedInstancesError`.
  const assigned = triggerConfig?.assignedInstances;
  const remappedConfig = Array.isArray(assigned)
    ? {
        ...triggerConfig,
        // Cross-site paste feeds clipboard JSON through here without Zod
        // validation, so a member that is not a `[string, string]` pair is
        // dropped rather than destructured or stored: destructuring throws in
        // the paste reducer, and a stored `null` throws later in the runtime
        // consumers, which read `path[0]` off every member.
        assignedInstances: assigned.flatMap(
          (entry: unknown): [string, string][] => {
            if (
              !Array.isArray(entry) ||
              entry.length !== 2 ||
              typeof entry[0] !== 'string' ||
              typeof entry[1] !== 'string'
            ) {
              return [];
            }
            return [
              [
                options?.destinationComponentId ?? remapByMap(entry[0], idMap),
                remapByMap(entry[1], idMap),
              ],
            ];
          }
        ),
      }
    : assigned !== undefined
      ? // The container can be malformed as well (`null`, `{}`), and every
        // consumer reads `.length` or iterates it. Normalized to the explicit
        // "drives none" rather than removed: an absent `assignedInstances`
        // means every popover the interaction names, so removing a corrupt
        // narrowing would widen it, where an unusable member above only
        // narrows it.
        {...triggerConfig, assignedInstances: []}
      : triggerConfig;

  if (!Array.isArray(targetDef) || !targetDef[0]) {
    if (remappedConfig === triggerConfig) return trigger;
    // The SAME ARITY back, not an unconditional three-item tuple. A genuinely
    // targetless trigger — a Page load, which has no `defaultTarget` — is
    // `[key, config]`, and `triggerTupleSchema` has no arm for a third slot
    // holding `undefined`: it serializes as `null`, so detach/import could
    // produce an interaction that failed schema validation even though its ids
    // were remapped correctly.
    return trigger.length < 3
      ? ([triggerKey, remappedConfig] as Trigger)
      : ([triggerKey, remappedConfig, targetDef] as Trigger);
  }

  const repaired = repairMissingFilterBy(targetDef);
  const extensionKey = repaired[0];
  const value = repaired[1];
  const filterContext = targetTransforms.getFilter(repaired);
  const remappedFilterContext = remapFilterContext(
    filterContext,
    idMap,
    styleBlocksIdMap,
    options
  );

  const buildTarget = (key: ExtensionKey, val: unknown) =>
    buildTargetDefinition(key, val, remappedFilterContext);
  const remappedTarget = buildTarget(
    extensionKey,
    remapTargetValue(extensionKey, value, idMap, styleBlocksIdMap, options)
  );

  return [triggerKey, remappedConfig, remappedTarget];
};
